import { PlayerAnalyticsConnector } from '@eyevinn/player-analytics-client-sdk-web';

export type VimondAnalyticsConfig = {
  eventsinkUrl: string;
  sessionId?: string;
  heartbeatInterval?: number;
  debug?: boolean;
  enableAdEvents?: boolean;
  adProfileId?: string;
  shardId?: string;
  player?: {
    muted?: () => boolean;
    autoplay?: () => boolean;
  };
};

export type VimondAdEventPayload = {
  data: unknown;
  adState: string;
  adPosition?: 'pre' | 'mid' | 'post';
  adMidQuartile?: string;
  adThirdQuartile?: string;
  completedAdsCount?: string;
  metadata?: Record<string, unknown>;
};

export type IMAAd = {
  data?: {
    adPodInfo?: { totalAds?: number };
    clickThroughUrl?: string;
  };
  getAdId?: () => string;
  getAdPodInfo?: () => unknown;
  getDuration?: () => number;
  getTitle?: () => string;
  isSkippable?: () => boolean;
};

export type IMAPodInfo = {
  getAdPosition?: () => number;
  getPodIndex?: () => number;
};

function isImaAd(obj: unknown): obj is IMAAd {
  return typeof obj === 'object' && obj !== null;
}

function isPodInfo(obj: unknown): obj is IMAPodInfo {
  return typeof obj === 'object' && obj !== null;
}

export const vimondAnalytics = (config: VimondAnalyticsConfig) => {
  let eyevinnConnector: PlayerAnalyticsConnector | null = null;
  let isInitialized = false;
  let adMidQuartileRef = '0';
  let adThirdQuartileRef = '0';
  let completedAdsCountRef = 0;

  const init = async () => {
    if (isInitialized) return;

    eyevinnConnector = new PlayerAnalyticsConnector(
      config.eventsinkUrl,
      config.debug
    );

    await eyevinnConnector.init({
      sessionId: config.sessionId,
      heartbeatInterval: config.heartbeatInterval ?? 10_000,
      shardId: config.shardId,
    });

    isInitialized = true;
  };

  const load = (videoElement: HTMLVideoElement) => {
    if (!eyevinnConnector || !isInitialized) {
      throw new Error(
        'VimondAnalytics must be initialized before loading video element'
      );
    }
    eyevinnConnector.load(videoElement);
  };

  const reportMetadata = (metadata: Record<string, unknown>) => {
    if (!eyevinnConnector || !isInitialized) return;
    const playerState = getPlayerState();
    const enrichedMetadata = {
      ...metadata,
      playerProfileId: getUrlParamFromWindow('playerId'),
      playerSource: getPlayerSource(),
      domain: typeof window !== 'undefined' ? window.location.hostname : '',
      muted: playerState.muted,
      autoplay: playerState.autoplay,
    };
    eyevinnConnector.reportMetadata(enrichedMetadata);
  };

  const reportAdEvent = (payload: VimondAdEventPayload) => {
    if (!config.enableAdEvents || !eyevinnConnector || !isInitialized) return;
    const metadataPayload = buildAdAnalyticsPayload(payload);
    eyevinnConnector.reportMetadata(metadataPayload);
  };

  const reportBitrateChange = (bitrate: number) => {
    if (!eyevinnConnector || !isInitialized) return;
    eyevinnConnector.reportBitrateChange({ bitrate });
  };

  const setAdMidQuartile = (value: string) => {
    adMidQuartileRef = value;
  };

  const setAdThirdQuartile = (value: string) => {
    adThirdQuartileRef = value;
  };

  const incrementCompletedAds = () => {
    completedAdsCountRef++;
  };

  const resetCompletedAds = () => {
    completedAdsCountRef = 0;
  };

  const getCompletedAdsCount = (): number => {
    return completedAdsCountRef;
  };

  const deinit = () => {
    if (eyevinnConnector) {
      eyevinnConnector.deinit();
      eyevinnConnector = null;
    }
    isInitialized = false;
  };

  const buildAdAnalyticsPayload = (payload: VimondAdEventPayload) => {
    let adImpressionId = '';
    let adTitle = '';
    let adDuration = '';
    let adTagUrl = '';
    let adTagType = 'VAST';
    let adPodPosition = '0';
    let adSkippable = '0';
    let adTotalRequest = '0';

    let imaAd: unknown = undefined;
    if (
      payload.data &&
      typeof payload.data === 'object' &&
      'getAd' in payload.data &&
      typeof (payload.data as { getAd?: () => unknown }).getAd === 'function'
    ) {
      imaAd = (payload.data as { getAd: () => unknown }).getAd();
    }

    if (isImaAd(imaAd)) {
      adImpressionId = imaAd.getAdId?.() ?? '';
      adTitle = imaAd.getTitle?.() ?? '';
      adDuration = Math.round((imaAd.getDuration?.() ?? 0) * 1000).toString();
      adTagUrl = imaAd.data?.clickThroughUrl ?? '';
      adTotalRequest = imaAd.data?.adPodInfo?.totalAds?.toString() ?? '0';

      const podInfo = imaAd.getAdPodInfo?.();
      if (isPodInfo(podInfo)) {
        adTagType = podInfo.getPodIndex?.() ? 'VMAP' : 'VAST';
        adPodPosition = podInfo.getAdPosition?.()?.toString() ?? '0';
      }

      adSkippable = imaAd.isSkippable?.() ? '1' : '0';
    }

    const playerSource = getPlayerSource();
    const playerState = getPlayerState();

    const adProfileId =
      playerSource === 'appbuilder'
        ? (config.adProfileId ?? '')
        : (getUrlParamFromWindow('profileId') ?? '');

    const { hostname, pathname } = getSafeLocation();
    const basePayload = {
      eventType: 'Ad',
      eventState: payload.adState,
      adImpressionId,
      playerSource,
      domain: hostname,
      path: pathname,
      adProfileId,
      playerProfileId: getUrlParamFromWindow('playerId'),
      adType: '1',
      adPosition: payload.adPosition ?? 'pre',
      adDuration,
      adTagType,
      adTagUrl,
      adTitle,
      adPodPosition,
      adSkippable,
      adMidQuartile: payload.adMidQuartile ?? adMidQuartileRef,
      adThirdQuartile: payload.adThirdQuartile ?? adThirdQuartileRef,
      adTotalRequest,
      completedAdsCount:
        payload.completedAdsCount ?? completedAdsCountRef.toString(),
      shardId: config.shardId,
      muted: playerState.muted,
      autoplay: playerState.autoplay,
    };

    // Add metadata fields if provided
    if (payload.metadata && typeof payload.metadata === 'object') {
      Object.entries(payload.metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          (basePayload as Record<string, unknown>)[key] = value;
        }
      });
    }

    return basePayload;
  };

  const getPlayerSource = (): string => {
    const isEmbeddedPath =
      typeof window !== 'undefined' &&
      window.location.pathname.includes('/embedded/');
    return isEmbeddedPath ? 'inline' : 'appbuilder';
  };

  const getUrlParamFromWindow = (param: string): string => {
    if (typeof window === 'undefined') return '';
    try {
      const { href } = getSafeLocation();
      if (!href) return '';
      const url = new URL(href);
      const params = new URLSearchParams(url.search);
      return params.get(param) ?? '';
    } catch (error) {
      console.warn(`Failed to extract ${param} from URL:`, error);
      return '';
    }
  };

  const getSafeLocation = (): {
    href: string;
    hostname: string;
    pathname: string;
  } => {
    try {
      const maybeWindow =
        typeof window !== 'undefined'
          ? (window as unknown as {
              location?: {
                href?: string;
                hostname?: string;
                pathname?: string;
              };
            })
          : undefined;
      const loc = maybeWindow?.location ?? {};
      return {
        href: loc.href ?? '',
        hostname: loc.hostname ?? '',
        pathname: loc.pathname ?? '',
      };
    } catch {
      return { href: '', hostname: '', pathname: '' };
    }
  };
  const getPlayerState = (): { muted: string; autoplay: string } => {
    let muted = '0';
    let autoplay = '0';

    if (config.player) {
      try {
        const isMuted = config.player.muted?.();
        muted = isMuted ? '1' : '0';
      } catch {
        // Silently ignore errors when getting muted state
      }

      try {
        const autoplayValue = config.player.autoplay?.();
        // autoplay returns boolean - true if enabled, false if disabled
        autoplay = autoplayValue ? '1' : '0';
      } catch {
        // Silently ignore errors when getting autoplay state
      }
    }

    return { muted, autoplay };
  };

  return {
    init,
    load,
    reportMetadata,
    reportAdEvent,
    reportBitrateChange,
    setAdMidQuartile,
    setAdThirdQuartile,
    incrementCompletedAds,
    resetCompletedAds,
    getCompletedAdsCount,
    deinit,
  };
};
